import {
  type CollisionDetection,
  DndContext,
  DragOverlay,
  type DragStartEvent,
  pointerWithin,
  rectIntersection,
} from "@dnd-kit/core";
import { useMemo, useState } from "react";
import type { Item, Project } from "../api/types";
import { useT } from "../i18n";
import { keyBetween, rootTasks, sectionsOf } from "../lib/tasks";
import { useSyncState } from "../store";
import { type Container, DroppableList, handleTaskDrop, SortableItem, useDndSensors } from "../tasks/dnd";
import { TaskRow } from "../tasks/TaskRow";
import { AddTask } from "../tasks/TaskTree";
import { usePrefs } from "../ui/prefs";
import { applySort } from "./grouping";
import { AddSection, SectionHeader } from "./sections";
import type { EffectiveViewOptions } from "./viewOptions";

/** Prefer the card/column under the pointer; fall back to rectangle overlap. */
const boardCollision: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  if (hits.length) {
    const card = hits.find((h) => !String(h.id).startsWith("container:"));
    return card ? [card] : hits;
  }
  return rectIntersection(args);
};

export function BoardView({
  project,
  options,
  completed,
}: {
  project: Project;
  options: EffectiveViewOptions;
  completed: Item[];
}) {
  const state = useSyncState();
  const prefs = usePrefs();
  const t = useT();
  const sensors = useDndSensors();
  const [dragging, setDragging] = useState<Item | null>(null);
  const sections = sectionsOf(state, project.id);
  const containers: Container[] = useMemo(
    () => [
      {
        id: "none",
        tasks: applySort(rootTasks(state, project.id, null), options, prefs.tz),
        target: { project_id: project.id, section_id: null, parent_id: null },
      },
      ...sectionsOf(state, project.id).map((s) => ({
        id: s.id,
        tasks: applySort(rootTasks(state, project.id, s.id), options, prefs.tz),
        target: { project_id: project.id, section_id: s.id, parent_id: null },
      })),
    ],
    [state, project.id, options, prefs.tz],
  );
  const manual = !options.sortedBy || options.sortedBy === "manual";
  const visible = containers.filter((c) => c.id !== "none" || c.tasks.length > 0 || sections.length === 0);
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={boardCollision}
      onDragStart={(e: DragStartEvent) => setDragging(state.items[String(e.active.id)] ?? null)}
      onDragCancel={() => setDragging(null)}
      onDragEnd={(e) => {
        setDragging(null);
        // Moving between columns always applies; reordering within one only in manual sort.
        if (manual) handleTaskDrop(e, containers);
        else {
          const over = e.over ? String(e.over.id) : "";
          const from = containers.find((c) => c.tasks.some((x) => x.id === String(e.active.id)));
          const to = containers.find((c) => over === `container:${c.id}` || c.tasks.some((x) => x.id === over));
          if (from && to && from.id !== to.id) handleTaskDrop(e, containers);
        }
      }}
    >
      <div className="board" data-testid="board">
        {visible.map((c) => {
          const section = c.id === "none" ? undefined : state.sections[c.id];
          const done = completed.filter((x) => (x.section_id ?? null) === (section?.id ?? null) && !x.parent_id);
          return (
            <div className="board-col" key={c.id} data-testid="board-column" data-section-name={section?.name ?? ""}>
              <SectionHeader section={section} title={section?.name ?? t("noSection")} count={c.tasks.length} />
              <DroppableList id={c.id} items={c.tasks.map((x) => x.id)} className="board-col-body" overClassName="over">
                {c.tasks.map((it) => (
                  <SortableItem key={it.id} id={it.id}>
                    {(handle, isDragging) => (
                      <div
                        className="card"
                        {...handle.attributes}
                        {...handle.listeners}
                        style={{ opacity: isDragging ? 0.4 : 1 }}
                      >
                        <TaskRow item={it} />
                      </div>
                    )}
                  </SortableItem>
                ))}
                {done.map((it) => (
                  <div className="card" key={it.id}>
                    <TaskRow item={it} />
                  </div>
                ))}
              </DroppableList>
              <AddTask defaults={{ projectId: project.id, sectionId: section?.id ?? null }} />
            </div>
          );
        })}
        <div className="board-col">
          <AddSection projectId={project.id} orderKey={keyBetween(sections.at(-1), undefined)} always />
        </div>
      </div>
      <DragOverlay>
        {dragging ? (
          <div className="card" style={{ width: 272 }}>
            <TaskRow item={dragging} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
